import { memo, useCallback, useMemo } from 'react';

import {
  type AirActionTrackingLocation,
  type TrackCopiedShareLinkParams,
  useTrackCopiedShareLink,
} from '@air/analytics';
import { type ShortUrlResponse } from '@air/api/types';
import { Button } from '@air/primitive-button';
import { type DropdownMenuOption } from '@air/primitive-dropdown-menu';
import { formatFullName } from '@air/utilities';

import { CopyLinkButton } from '~/components/ShareModal/components/ShareLink/CopyLinkButton';
import { ShareLink } from '~/components/ShareModal/components/ShareLink/ShareLink';
import { useWorkspaceMembers } from '~/swr-hooks/members/useWorkspaceMembers';
import { getFullShareUrl } from '~/utils/shareUrl';
import { buildShareUrlCopiedAnalyticsParams } from '~/utils/shortUrl/buildShareUrlCopiedAnalyticsParams';

export type EditableShareLinkProps = {
  createdAt?: ShortUrlResponse['createdAt'];
  createdBy?: ShortUrlResponse['createdBy'];
  objectId: string;
  objectType: TrackCopiedShareLinkParams['type'];
  onRegenerate?: () => void;
  shareId: ShortUrlResponse['id'];
  shortIdInfo: ShortUrlResponse;
  trackLocation: AirActionTrackingLocation;
};

export const EditableShareLink = memo(
  ({
    createdAt,
    createdBy,
    objectId,
    objectType,
    onRegenerate,
    shareId,
    shortIdInfo,
    trackLocation,
  }: EditableShareLinkProps) => {
    const { trackCopiedShareLink } = useTrackCopiedShareLink();
    const { members } = useWorkspaceMembers();
    const createdByName = useMemo(() => {
      const member = members?.allMembers.find((m) => m.accountId === createdBy);

      return member ? formatFullName(member.firstName, member.lastName) || member.email || undefined : undefined;
    }, [createdBy, members]);

    const shareUrl = getFullShareUrl(shareId);

    const handleCopied = useCallback(() => {
      trackCopiedShareLink({
        id: objectId,
        shareId,
        type: objectType,
        location: trackLocation,
        ...buildShareUrlCopiedAnalyticsParams({ shareId, shortUrl: shortIdInfo, objectType }),
      });
    }, [trackCopiedShareLink, objectId, shareId, objectType, trackLocation, shortIdInfo]);

    const handlePreviewClick = useCallback(() => {
      window.open(shareUrl, '_blank', 'noopener,noreferrer');
    }, [shareUrl]);

    const menuActions = useMemo<DropdownMenuOption[] | undefined>(
      () =>
        onRegenerate
          ? [
              {
                id: 'regenerate',
                label: 'Generate a new link',
                type: 'item',
                onSelect: () => onRegenerate(),
              },
            ]
          : undefined,
      [onRegenerate],
    );

    return (
      <div className="flex items-center gap-2">
        <div className="min-w-0 flex-1">
          <ShareLink
            createdAt={createdAt}
            createdByName={createdByName}
            shareUrl={shareUrl}
            menuActions={menuActions}
          />
        </div>
        <Button
          appearance="filled"
          color="gray"
          data-testid="SHARE_LINK_PREVIEW_BUTTON"
          onClick={handlePreviewClick}
          size="medium"
          type="button"
        >
          Preview
        </Button>
        <CopyLinkButton shareUrl={shareUrl} onCopied={handleCopied} />
      </div>
    );
  },
);

EditableShareLink.displayName = 'EditableShareLink';
